import { type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { AsyncSelectField } from "@/components/form";
import type { SearchableOption } from "@/components/SearchableSelect";
import { merchants, type MerchantListItem } from "@/features/merchants/api/merchants.api";

const MERCHANT_PAGE_SIZE = 20;

function toOption(m: Pick<MerchantListItem, "id" | "mid" | "dbaName" | "city" | "state">): SearchableOption {
  const place = [m.city, m.state].filter(Boolean).join(", ");
  return {
    value: m.id,
    label: `${m.dbaName} (${m.mid})`,
    hint: place || undefined,
  };
}

export async function searchMerchantOptions({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
  const page = await merchants.list({
    keyword,
    pageNumber,
    pageSize: MERCHANT_PAGE_SIZE,
    sort: [{ by: "dbaName", dir: "asc" }],
  });
  return {
    options: page.items.map(toOption),
    hasNextPage: page.hasNextPage,
    totalCount: page.totalCount,
  };
}

export async function resolveMerchantOption(id: string): Promise<SearchableOption | null> {
  const m = await merchants.get(id);
  return m?.id ? toOption(m) : null;
}

export function MerchantSelectField<T extends FieldValues>({
  control, name, label = "Merchant", required, placeholder, hint, className, disabled, onValueMissing, onError,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  onValueMissing?: () => void;
  onError?: (error: unknown) => void;
}) {
  return (
    <AsyncSelectField
      control={control}
      name={name}
      label={label}
      required={required}
      placeholder={placeholder ?? "Select a merchant…"}
      hint={hint}
      className={className}
      disabled={disabled}
      search={searchMerchantOptions}
      resolveValue={resolveMerchantOption}
      onValueMissing={onValueMissing}
      onError={onError}
    />
  );
}
